import React, { FC, useEffect, useState } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useLocation, useNavigate, useRoutes } from "react-router-dom";
import { useRecoilState, useSetRecoilState } from "recoil";
import { Helmet } from "react-helmet-async";
import extensibilitySdk from "@outreach/extensibility-sdk";

import {
  convertDbAccountToAppAccount,
  convertDbOrganizationSettingsToAppOrganizationSettings,
  getDbUser,
  getDbOrganization,
  getMyOrganization,
  getOrganizationIdByOutreachOrgId,
  getOutreachAuthUrl,
  getHubSpotAuthUrl,
  onAuthStateChangedHook,
} from "./services/firebase";
import { Organization } from "./types/organizations";
import { isDevelopment, isStaging } from "./services/config";
import { getRoutes } from "./services/routes";
import { CONNECT_EXTENSION_PATH, POST_LOGIN_PATH_KEY } from "./services/extension";
import { LOCAL_STORAGE_KEYS } from "./common/consts";
import { devLog, devWarn } from "./common/utils/devLog";
import {
  extensibilitySdkInitializedAtom,
  outreachOrgIdAtom,
  organizationAtom,
  signedInUserAtom,
} from "./state/atoms";
import Loading, { LoadingVariant } from "./components/Display/Loading";
import EnvLabel from "./components/Layout/EnvLabel";
import AppContainer from "./components/Layout/AppContainer";
import CrmMuiThemeProvider from "./components/CrmUi/CrmMuiThemeProvider";
import SupportChat from "./components/Layout/SupportChat";
import StatusBar from "./components/Display/StatusBar";
import { isEmbeddedPath } from "./services/embedded";

const MCP_AUTHORIZE_PATH = "/mcp/authorize";

// Runs before the router can redirect a signed-out visitor to /auth/login.
if (window.location.pathname.startsWith(CONNECT_EXTENSION_PATH)) {
  sessionStorage.setItem(POST_LOGIN_PATH_KEY, CONNECT_EXTENSION_PATH);
}
// An AI app's sign-in (the MCP consent page) comes back here after login, with its request.
if (window.location.pathname.startsWith(MCP_AUTHORIZE_PATH)) {
  sessionStorage.setItem(POST_LOGIN_PATH_KEY, `${MCP_AUTHORIZE_PATH}${window.location.search}`);
}

const App: FC = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [waitingForAuth, setWaitingForAuth] = useState(true);
  const [registeredOrgRedirected, setRegisteredOrgRedirected] = useState(false);
  const [isOrgRegistered, setIsOrgRegistered] = useState(false);
  const [signedInUser, setSignedInUser] = useRecoilState(signedInUserAtom);
  const setOrganization = useSetRecoilState(organizationAtom);
  const setExtensibilitySdkInitialized = useSetRecoilState(
    extensibilitySdkInitializedAtom
  );
  const [outreachOrgId, setOutreachOrgId] = useRecoilState(outreachOrgIdAtom);

  const env = isDevelopment ? "Development" : isStaging ? "Staging" : null;
  const isEmbeddedSurface = isEmbeddedPath(pathname);

  const content = useRoutes(getRoutes(!!signedInUser));

  useEffect(() => {
    const initExtensibilitySdk = async () => {
      try {
        const ctx = await extensibilitySdk.init();
        setExtensibilitySdkInitialized(true);
        devLog("Extensibility SDK initialized");
        // Re-apply data-theme after SDK init (SDK may overwrite it)
        // Outreach frames are always light, whatever the dashboard is set to.
        const embedded = isEmbeddedPath(window.location.pathname);
        const savedTheme = embedded ? "light" : localStorage.getItem("theme-mode") || "light";
        document.documentElement.setAttribute("data-theme", savedTheme);

        const outreachOrganizationId = ctx?.organization?.id;
        if (outreachOrganizationId) {
          setOutreachOrgId(outreachOrganizationId);

          const response = await getOrganizationIdByOutreachOrgId({
            outreachOrganizationId,
          });
          if (response?.result?.organizationId) {
            setIsOrgRegistered(true);
          }
        }
      } catch (err) {
        console.error("Error initializing Extensibility SDK", { err });
        setExtensibilitySdkInitialized(false);
      }
    };

    initExtensibilitySdk();
  }, [setExtensibilitySdkInitialized, setOutreachOrgId]);

  useEffect(() => {
    // Outreach loads each extension at its own path. Sending those to the
    // Insights view would mean the tab and the tile both render the dashboard.
    const isEmbeddedSurface = isEmbeddedPath(window.location.pathname);
    if (outreachOrgId && isOrgRegistered && !registeredOrgRedirected && !isEmbeddedSurface) {
      navigate(`/view/${outreachOrgId}`);
      setRegisteredOrgRedirected(true);
      return;
    }
    const unsubscribe = onAuthStateChangedHook(async (authUser) => {
      try {
        setWaitingForAuth(true);

        if (!authUser) {
          setSignedInUser(null);
          return;
        }

        const { uid, email: authEmail } = authUser;

        if (!authEmail) {
          console.error("Authenticated user has no email");
          return;
        }

        // Get user first to check if they already have an organization
        // This helps us decide if we need to process OAuth code
        let dbUser = await getDbUser(uid);
        
        // Check for OAuth codes and process them immediately if present
        // Process both Outreach and HubSpot OAuth codes
        // Parse JSON stringified values from useLocalStorage hook
        const outreachAuthCodeRaw = localStorage.getItem(LOCAL_STORAGE_KEYS.outreachAuthCode);
        const hubspotAuthCodeRaw = localStorage.getItem(LOCAL_STORAGE_KEYS.hubspotAuthCode);
        const hubspotRedirectUriRaw = localStorage.getItem(LOCAL_STORAGE_KEYS.hubspotRedirectUri);
        
        // Parse JSON (useLocalStorage stores values as JSON strings)
        // Handle empty strings, null, and proper JSON strings
        const parseLocalStorageValue = (value: string | null): string | null => {
          if (!value || value === 'null' || value === '""' || value === "''") return null;
          try {
            // If it's a JSON string, parse it
            if ((value.startsWith('"') && value.endsWith('"')) || (value.startsWith("'") && value.endsWith("'"))) {
              const parsed = JSON.parse(value);
              // Only return if it's a non-empty string
              return parsed && typeof parsed === 'string' && parsed.length > 0 ? parsed : null;
            }
            return value.length > 0 ? value : null;
          } catch {
            // If parsing fails, return the raw value if it's not empty
            return value.length > 0 ? value : null;
          }
        };
        
        // OAuth processing is handled by hooks in Dashboard.tsx
        // App.tsx only checks for pending organization and fetches if needed
        
        // Clear stuck processing flag if it's been there too long (safety net)
        const oauthProcessingFlag = localStorage.getItem("oauthCodeProcessing");
        if (oauthProcessingFlag === "true") {
          const processingTime = localStorage.getItem("oauthCodeProcessingTime");
          if (processingTime) {
            const elapsed = Date.now() - parseInt(processingTime, 10);
            if (elapsed > 15000) {
              devWarn("⚠️ OAuth processing flag stuck for >15s, clearing it");
              localStorage.removeItem("oauthCodeProcessing");
              localStorage.removeItem("oauthCodeProcessingTime");
            }
          } else {
            // No timestamp, clear it
            devWarn("⚠️ OAuth processing flag exists but no timestamp, clearing it");
            localStorage.removeItem("oauthCodeProcessing");
          }
        }
        
        // OAuth just failed: skip redirect to prevent loop, but still fetch org and set user so dashboard shows
        const oauthCodeFailedRaw = localStorage.getItem("oauthCodeFailed");
        let oauthCodeFailed = !!oauthCodeFailedRaw;
        const oauthFailedAt = localStorage.getItem("oauthCodeFailedAt");
        if (oauthCodeFailed && oauthFailedAt) {
          const elapsed = Date.now() - parseInt(oauthFailedAt, 10);
          if (elapsed > 90_000) {
            localStorage.removeItem("oauthCodeFailed");
            localStorage.removeItem("oauthCodeFailedAt");
            oauthCodeFailed = false;
          }
        }
        if (oauthCodeFailed) {
          devLog("⏳ OAuth code failed recently, will skip OAuth redirect but continue org lookup");
        }
        
        let organization: Awaited<ReturnType<typeof getMyOrganization>>["result"] = undefined;
        
        // First, check if we have a pending organization from OAuth (stored in localStorage)
        // This avoids refetching before Firestore writes propagate
        try {
          const pendingOrgStr = localStorage.getItem(LOCAL_STORAGE_KEYS.pendingOrganization);
          devLog("Checking for pending organization:", { 
            hasPendingOrg: !!pendingOrgStr, 
            key: LOCAL_STORAGE_KEYS.pendingOrganization 
          });
          if (pendingOrgStr) {
            const pendingOrg = JSON.parse(pendingOrgStr) as Organization;
            devLog("Found pending organization:", { 
              id: pendingOrg?.id, 
              name: pendingOrg?.name,
              isOutreachAuthorized: pendingOrg?.isOutreachAuthorized 
            });
            if (pendingOrg?.id) {
              organization = pendingOrg;
              devLog(`✅ Using pending organization from OAuth: ${pendingOrg.id}`);
              
              // CRITICAL: Refetch user data after OAuth to get updated organizationId and auth flags
              // The backend has updated the user document, so we need fresh data
              try {
                devLog("🔄 Refetching user data after OAuth to get updated organizationId and auth flags...");
                const refreshedUser = await getDbUser(uid);
                if (refreshedUser) {
                  dbUser = refreshedUser;
                  devLog("✅ Refreshed user data after OAuth:", {
                    organizationId: refreshedUser.organizationId,
                    isOutreachAuthorized: refreshedUser.isOutreachAuthorized,
                    isHubSpotAuthorized: refreshedUser.isHubSpotAuthorized,
                  });
                } else {
                  devWarn("⚠️ Failed to refresh user data after OAuth, using existing dbUser");
                }
              } catch (error) {
                console.error("❌ Error refreshing user data after OAuth:", error);
                // Continue with existing dbUser - better than nothing
              }
              
              // Set organization in Recoil state immediately to update UI
              // Convert it properly first (same logic as below)
              const convertedOrg = convertDbOrganizationSettingsToAppOrganizationSettings(pendingOrg);
              const isOutreachAuthed = pendingOrg.isOutreachAuthorized === true || !!pendingOrg.outreachAuthentication;
              const isHubSpotAuthed = pendingOrg.isHubSpotAuthorized === true || !!pendingOrg.hubSpotAuthentication;
              setOrganization({
                ...convertedOrg,
                id: pendingOrg.id,
                name: pendingOrg.name,
                isOutreachAuthorized: isOutreachAuthed,
                isHubSpotAuthorized: isHubSpotAuthed,
                outreachAuthentication: pendingOrg.outreachAuthentication,
                hubSpotAuthentication: pendingOrg.hubSpotAuthentication,
                prospectsWebhookId: pendingOrg.prospectsWebhookId,
                initialIntegrationType: pendingOrg.initialIntegrationType,
              } as any);
              // Clear it after using it
              localStorage.removeItem(LOCAL_STORAGE_KEYS.pendingOrganization);
              // Clear processing flag since we have the org
              localStorage.removeItem("oauthCodeProcessing");
              localStorage.removeItem("oauthCodeProcessingTime");
            } else {
              devWarn("Pending organization missing id, clearing it");
              localStorage.removeItem(LOCAL_STORAGE_KEYS.pendingOrganization);
            }
          } else {
            devLog("No pending organization found in localStorage");
          }
        } catch (error) {
          console.error("Failed to read pending organization:", error);
          localStorage.removeItem(LOCAL_STORAGE_KEYS.pendingOrganization);
        }
        
        // Retry logic: After OAuth, organization might not be ready yet
        // Retry up to 3 times with exponential backoff (reduced since we check pending org first)
        const maxRetries = 3;
        for (let attempt = 0; attempt < maxRetries && !organization; attempt++) {
          // Refresh user data first to get latest organizationId (critical after OAuth)
          if (attempt > 0) {
            try {
              const refreshedUser = await getDbUser(uid);
              if (refreshedUser?.organizationId && dbUser && (!dbUser.organizationId || refreshedUser.organizationId !== dbUser.organizationId)) {
                dbUser.organizationId = refreshedUser.organizationId;
                devLog(`Refreshed user data, organizationId: ${refreshedUser.organizationId}`);
              }
            } catch (error) {
              devWarn("Failed to refresh user data:", error);
            }
          }
          
          // Primary: Use getMyOrganization (now checks user doc's organizationId as fallback)
          try {
            const orgResult = await getMyOrganization();
            if (orgResult?.result) {
              organization = orgResult.result;
              devLog(`Found organization via getMyOrganization: ${organization.id}`);
              break; // Found it, exit retry loop
            } else {
              devWarn(`getMyOrganization returned no result (attempt ${attempt + 1}/${maxRetries})`);
            }
          } catch (error) {
            devWarn(`getMyOrganization failed (attempt ${attempt + 1}/${maxRetries}):`, error);
          }
          
          // Fallback: If user has organizationId, try direct fetch (if security rules allow)
          if (!organization && dbUser?.organizationId) {
            try {
              const directOrg = await getDbOrganization(dbUser.organizationId);
              if (directOrg) {
                organization = directOrg;
                devLog(`Found organization via direct fetch: ${directOrg.id}`);
                break; // Found it, exit retry loop
              }
            } catch (error) {
              // Silently fail - security rules may not allow direct access
              devWarn(`Direct fetch failed for orgId ${dbUser.organizationId}:`, error);
            }
          }
          
          // If still not found and not last attempt, wait before retrying
          if (!organization && attempt < maxRetries - 1) {
            const delay = Math.min(1000 * Math.pow(2, attempt), 5000); // Exponential backoff, max 5s
            devLog(`Organization not found, retrying in ${delay}ms... (attempt ${attempt + 1}/${maxRetries})`);
            await new Promise(resolve => setTimeout(resolve, delay));
          }
        }
        
        const appUser = convertDbAccountToAppAccount(dbUser, organization);

        setSignedInUser(appUser);

        // Signup matched an existing organization and waits for a verified
        // email; the verify step lives on the select-integration page.
        if (appUser?.pendingOrgJoin && !organization && pathname !== "/app/select-integration" && !pathname.startsWith("/oauth")) {
          navigate("/app/select-integration");
          return;
        }
        
        // Set organization in Recoil state if we have it
        if (organization) {
          setOrganization(organization);
        }

        if (organization) {
          // Organization already extends OrganizationSettings, so we can use it directly
          // The conversion function will handle the customField mapping
          const convertedOrg = convertDbOrganizationSettingsToAppOrganizationSettings(organization);
          // Preserve auth flags and other Organization-specific fields
          // IMPORTANT: Make sure auth flags are explicitly set from the organization data
          // Ensure auth flags are properly set from the organization data
          // If the flags exist in Firestore, use them; otherwise derive from auth objects
          const isOutreachAuthed = organization.isOutreachAuthorized === true || !!organization.outreachAuthentication;
          const isHubSpotAuthed = organization.isHubSpotAuthorized === true || !!organization.hubSpotAuthentication;
          
          setOrganization({
            ...convertedOrg,
            id: organization.id,
            name: organization.name,
            status: organization.status,
            isOutreachAuthorized: isOutreachAuthed,
            outreachAuthentication: organization.outreachAuthentication,
            isHubSpotAuthorized: isHubSpotAuthed,
            hubSpotAuthentication: organization.hubSpotAuthentication,
            prospectsWebhookId: organization.prospectsWebhookId,
            hubSpotWebhookId: organization.hubSpotWebhookId,
            isEnrichmentEnabled: organization.isEnrichmentEnabled,
            initialIntegrationType: organization.initialIntegrationType,
          });
          
          devLog("App.tsx: Set organization with auth flags:", {
            id: organization.id,
            isOutreachAuthorized: organization.isOutreachAuthorized,
            isHubSpotAuthorized: organization.isHubSpotAuthorized,
            hasOutreachAuth: !!organization.outreachAuthentication,
            hasHubSpotAuth: !!organization.hubSpotAuthentication,
            rawOrgData: {
              isOutreachAuthorized: organization.isOutreachAuthorized,
              isHubSpotAuthorized: organization.isHubSpotAuthorized,
              outreachAuth: !!organization.outreachAuthentication,
              hubSpotAuth: !!organization.hubSpotAuthentication,
            }
          });
        } else {
          devWarn("No organization data found in database after retries");
        }

        const {
          isTrial,
          isOutreachAuthorized,
          isHubSpotAuthorized,
          isPayingCustomer,
          isFinishedOnboarding,
          organizationId,
        } = appUser || {};

        if (appUser && organization) {
          if (pathname.startsWith("/oauth")) {
            return;
          }

          // If already authed with Outreach or HubSpot, go straight to dashboard
          // Check both user and organization auth status (organization is source of truth)
          // Use isOutreachAuthorized/isHubSpotAuthorized flags (they exist in Firestore)
          // Also check auth objects as fallback
          const orgIsOutreachAuthed = organization?.isOutreachAuthorized === true || !!organization?.outreachAuthentication;
          const orgIsHubSpotAuthed = organization?.isHubSpotAuthorized === true || !!organization?.hubSpotAuthentication;
          const isActuallyAuthed = isOutreachAuthorized || isHubSpotAuthorized || orgIsOutreachAuthed || orgIsHubSpotAuthed;
          
          devLog("App.tsx: Checking if user is authed:", {
            userIsOutreachAuthed: isOutreachAuthorized,
            userIsHubSpotAuthed: isHubSpotAuthorized,
            orgIsOutreachAuthorized: organization?.isOutreachAuthorized,
            orgIsHubSpotAuthorized: organization?.isHubSpotAuthorized,
            orgIsOutreachAuthed,
            orgIsHubSpotAuthed,
            hasOutreachAuthObj: !!organization?.outreachAuthentication,
            hasHubSpotAuthObj: !!organization?.hubSpotAuthentication,
            isActuallyAuthed,
            pathname,
          });
          
          if (isActuallyAuthed) {
            // If user is already on an app page, processing OAuth, or inside an
            // Outreach extension frame, let them stay. The embedded surfaces
            // under /outreach/ and /hubspot/ must never turn into the dashboard.
            if (pathname.startsWith("/app/") || pathname.startsWith("/oauth/") || pathname.startsWith(MCP_AUTHORIZE_PATH) || isEmbeddedPath(pathname)) {
              devLog("✅ User is authed and on app/oauth page, staying put:", pathname);
            } else {
              // The extension sends signed-out users to /app/connect-extension,
              // which the router bounces to login. Honor that intent after sign-in.
              const postLoginPath = sessionStorage.getItem(POST_LOGIN_PATH_KEY);
              if (postLoginPath) {
                sessionStorage.removeItem(POST_LOGIN_PATH_KEY);
                devLog("✅ User is authed, returning to requested page", postLoginPath);
                navigate(postLoginPath);
              } else {
                // Only redirect to dashboard if not already on an app or oauth page
                devLog("✅ User is authed, navigating to dashboard");
                navigate("/app/dashboard");
              }
            }
            return;
          }

          // If user has organization but isn't authed, redirect to OAuth
          // OR if user has no org yet, they need to connect - redirect to appropriate OAuth
          // But only if we're not coming from an OAuth callback (check URL)
          // AND only if there's no OAuth code being processed
          // Check for OAuth codes (check raw values to avoid parsing overhead)
          const outreachCodeCheck = localStorage.getItem(LOCAL_STORAGE_KEYS.outreachAuthCode);
          const hubspotCodeCheck = localStorage.getItem(LOCAL_STORAGE_KEYS.hubspotAuthCode);
          const hasOAuthCode = (outreachCodeCheck && outreachCodeCheck !== '""' && outreachCodeCheck !== 'null') || 
                               (hubspotCodeCheck && hubspotCodeCheck !== '""' && hubspotCodeCheck !== 'null');
          const oauthProcessing = localStorage.getItem("oauthCodeProcessing");
          const oauthJustFailed = localStorage.getItem("oauthCodeFailed");
          const hasPendingOrg = localStorage.getItem(LOCAL_STORAGE_KEYS.pendingOrganization);
          
          devLog("App.tsx: Checking OAuth redirect conditions:", {
            hasOrganization: !!organization,
            hasOrganizationId: !!organizationId,
            isOAuthPath: pathname.includes("/oauth"),
            hasOAuthCode: !!hasOAuthCode,
            oauthProcessing: !!oauthProcessing,
            oauthJustFailed: !!oauthJustFailed,
            hasPendingOrg: !!hasPendingOrg,
            shouldRedirect: (organization || organizationId) && !pathname.includes("/oauth") && !hasOAuthCode && !oauthProcessing && !oauthJustFailed && !hasPendingOrg
          });
          
          // Don't redirect if we just completed OAuth (pending org means we're processing it)
          if (hasPendingOrg) {
            devLog("⏳ Pending organization found, skipping OAuth redirect - App.tsx is processing it");
            return;
          }
          
          // Don't redirect if user is on the select-integration page (let them choose their integration)
          if (pathname === "/app/select-integration") {
            devLog("📋 User is on select-integration page, letting them choose");
            return;
          }
          
          if ((organization || organizationId) && !pathname.includes("/oauth") && !hasOAuthCode && !oauthProcessing && !oauthJustFailed && !hasPendingOrg) {
            // Check if organization is set to HubSpot, otherwise default to Outreach
            // IMPORTANT: If organization exists, use its initialIntegrationType
            // If organization doesn't exist yet, check user document for organizationId and fetch org
            let isHubSpotOrg = organization?.initialIntegrationType === "hubspot";
            
            // If organization not loaded but user has organizationId, try to fetch it
            if (!organization && dbUser?.organizationId) {
              try {
                const fetchedOrg = await getDbOrganization(dbUser.organizationId);
                if (fetchedOrg) {
                  isHubSpotOrg = fetchedOrg.initialIntegrationType === "hubspot";
                  devLog("App.tsx: Fetched organization to determine integration type:", {
                    organizationId: fetchedOrg.id,
                    initialIntegrationType: fetchedOrg.initialIntegrationType,
                    isHubSpotOrg
                  });
                }
              } catch (error) {
                devWarn("Failed to fetch organization to determine integration type:", error);
              }
            }
            
            devLog("App.tsx: Determining OAuth redirect:", {
              hasOrganization: !!organization,
              hasDbUserOrgId: !!dbUser?.organizationId,
              initialIntegrationType: organization?.initialIntegrationType,
              isHubSpotOrg,
              organizationId
            });
            
            // Redirect immediately to appropriate OAuth - no intermediate pages, no loops
            try {
              if (isHubSpotOrg) {
                // HubSpot org - redirect to HubSpot OAuth
                devLog("🔄 Redirecting to HubSpot OAuth");
                const redirectUri = `${window.location.origin}/oauth/hubspot`.replace(/\/$/, '');
                const { result, status } = await getHubSpotAuthUrl(redirectUri);
                if (status === 200 && result?.hubspotAuthUrl) {
                  window.location.href = result.hubspotAuthUrl;
                  return;
                }
              } else {
                // Default to Outreach OAuth (for new signups or Outreach orgs)
                devLog("🔄 Redirecting to Outreach OAuth (default or Outreach org)");
                const { result, status } = await getOutreachAuthUrl();
                if (status === 200 && result?.outreachAuthUrl) {
                  window.location.href = result.outreachAuthUrl;
                  return;
                }
              }
            } catch (error) {
              console.error("Failed to get OAuth URL:", error);
            }
            // If OAuth URL fetch fails, still don't show connect page - just wait
            // The user will need to refresh or the OAuth will complete
            return;
          }
          
          // If we're on OAuth callback page, don't redirect - let it process
          if (pathname.includes("/oauth")) {
            return;
          }

          // Connecting an AI app does not need the CRM connected: stay on (or go back to) its consent page.
          const pendingMcp = sessionStorage.getItem(POST_LOGIN_PATH_KEY);
          if (pathname.startsWith(MCP_AUTHORIZE_PATH)) return;
          if (pendingMcp?.startsWith(MCP_AUTHORIZE_PATH)) {
            sessionStorage.removeItem(POST_LOGIN_PATH_KEY);
            navigate(pendingMcp);
            return;
          }

          // Only navigate if not already on dashboard to prevent loops, and
          // never out of an Outreach or HubSpot frame.
          if (pathname !== "/app/dashboard" && !isEmbeddedPath(pathname)) {
            navigate("/app/dashboard");
          }
          return;
        }
        
        // Handle case where user exists but organization isn't loaded yet
        // If user has organizationId, redirect to OAuth (they need to complete auth)
        // But only if there's no OAuth code being processed
        // Check for OAuth codes (check raw values to avoid parsing overhead)
        const outreachCodeCheck2 = localStorage.getItem(LOCAL_STORAGE_KEYS.outreachAuthCode);
        const hubspotCodeCheck2 = localStorage.getItem(LOCAL_STORAGE_KEYS.hubspotAuthCode);
        const hasOAuthCode = (outreachCodeCheck2 && outreachCodeCheck2 !== '""' && outreachCodeCheck2 !== 'null') || 
                             (hubspotCodeCheck2 && hubspotCodeCheck2 !== '""' && hubspotCodeCheck2 !== 'null');
        const oauthProcessing = localStorage.getItem("oauthCodeProcessing");
        const oauthJustFailed = localStorage.getItem("oauthCodeFailed");
        
        if (appUser && !organization && organizationId && !pathname.includes("/oauth") && !hasOAuthCode && !oauthProcessing && !oauthJustFailed && pathname !== "/app/select-integration") {
          devLog("App.tsx: User has organizationId but organization not loaded, redirecting to OAuth");
          try {
            // Default to Outreach OAuth (most common case)
            const { result, status } = await getOutreachAuthUrl();
            if (status === 200 && result?.outreachAuthUrl) {
              window.location.href = result.outreachAuthUrl;
              return;
            }
          } catch (error) {
            console.error("Failed to get OAuth URL:", error);
          }
          return;
        }
      } catch (err) {
        console.error("Error logging in", { err });
        setSignedInUser(null);
      } finally {
        setWaitingForAuth(false);
      }
    });
    return () => {
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, outreachOrgId, registeredOrgRedirected, isOrgRegistered]);

  // Check if OAuth is processing (onboarding flow)
  const isOAuthProcessing = localStorage.getItem("oauthCodeProcessing") === "true";
  
  return (
    <>
      {isOAuthProcessing && <StatusBar />}
      {waitingForAuth && !isEmbeddedSurface ? (
        <div className="d-flex w-100 min-h-100vh align-items-center justify-content-center">
          <Loading variant={LoadingVariant.CIRCULAR} />
        </div>
      ) : (
        <>
          <Helmet>
            <title>Dashboard | boring</title>
          </Helmet>
          <CrmMuiThemeProvider>
            <AppContainer flush={isEmbeddedSurface}>
              {env && !isEmbeddedSurface && <EnvLabel env={env} />}
              {content}
              <ToastContainer position="top-left" />
              <SupportChat hidden={isEmbeddedSurface} />
            </AppContainer>
          </CrmMuiThemeProvider>
        </>
      )}
    </>
  );
};

export default App;
